import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ClipboardIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { Input } from '../input'
import { SegmentedControl } from '../segmented-control'
import { useToast } from '../toast'
import { permissionSummary } from '../bot-tokens/bot-tokens-section'
import { mcpUrl, MCP_DOC_URLS } from 'src/config/mcp'
import { usePermissions } from 'src/hooks/use-permissions'
import { botTokenQueries } from 'src/services/bot-tokens'

type AssistantClient = 'claude' | 'chatgpt'

// Mirrors docs/mcp-setup.md; only the first step differs per client.
const SHARED_STEP_KEYS = ['paste', 'login', 'permissions', 'approve'] as const

interface ConnectAssistantSectionProps {
  orgId: string
}

/** Lists connector bots (OAuth-created) for admins, who can revoke them in Bot tokens. */
const ActiveConnections: React.FC<{ orgId: string }> = ({ orgId }) => {
  const { t } = useTranslation()
  const { data } = useQuery(botTokenQueries.list({ filters: { organization: orgId } }))
  const connections = (data?.results ?? []).filter((bot) => bot.connectionClientName)

  if (!connections.length) {
    return (
      <p className="text-sm text-text-placeholder" data-testid="connect-assistant-no-connections">
        {t('connectAssistant.noConnections')}
      </p>
    )
  }

  return (
    <ul
      className="divide-y divide-border rounded-md border border-border"
      data-testid="connect-assistant-connections"
    >
      {connections.map((bot) => (
        <li
          key={bot.id}
          className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm"
          data-testid={`connection-${bot.id}`}
        >
          <div>
            <p className="font-medium text-text-primary">{bot.connectionClientName}</p>
            <p className="text-xs text-text-placeholder">
              {bot.created &&
                t('connectAssistant.connectedOn', {
                  date: format(new Date(bot.created), 'MMM d, yyyy'),
                })}
              {' · '}
              {permissionSummary(bot.permissions, t)}
            </p>
          </div>
          <a href="#bot-tokens-section" className="text-sm text-primary hover:underline">
            {t('connectAssistant.revokeLink')}
          </a>
        </li>
      ))}
    </ul>
  )
}

export const ConnectAssistantSection: React.FC<ConnectAssistantSectionProps> = ({ orgId }) => {
  const { t, i18n } = useTranslation()
  const { toast } = useToast()
  const { hasPermission } = usePermissions()
  const [client, setClient] = useState<AssistantClient>('claude')
  const docUrl = MCP_DOC_URLS[i18n.language?.startsWith('es') ? 'es' : 'en']

  const handleCopy = async () => {
    await navigator.clipboard.writeText(mcpUrl)
    toast({ description: t('connectAssistant.urlCopied'), type: 'success' })
  }

  return (
    <div
      className="rounded-lg border border-border bg-surface p-6 shadow-sm"
      data-testid="connect-assistant-section"
    >
      <h3 className="text-lg font-medium text-text-primary">{t('connectAssistant.title')}</h3>
      <p className="mt-1 text-sm text-text-placeholder">{t('connectAssistant.description')}</p>

      <div className="mt-4 flex items-end gap-2">
        <Input
          readOnly
          label={t('connectAssistant.urlLabel')}
          value={mcpUrl}
          className="font-mono text-xs"
          onFocus={(e) => e.target.select()}
          data-testid="connect-assistant-url"
        />
        <Button
          variant="ghost"
          iconOnly
          aria-label={t('connectAssistant.copyUrl')}
          title={t('connectAssistant.copyUrl')}
          onClick={handleCopy}
          className="shrink-0"
          data-testid="connect-assistant-copy"
        >
          <ClipboardIcon className="h-5 w-5" aria-hidden="true" />
        </Button>
      </div>

      <div className="mt-6">
        <SegmentedControl<AssistantClient>
          aria-label={t('connectAssistant.clientPicker')}
          value={client}
          onChange={setClient}
          size="sm"
          options={[
            {
              value: 'claude',
              label: t('connectAssistant.claude'),
              'data-testid': 'connect-assistant-claude',
            },
            {
              value: 'chatgpt',
              label: t('connectAssistant.chatgpt'),
              'data-testid': 'connect-assistant-chatgpt',
            },
          ]}
        />
        <ol
          className="mt-3 list-decimal space-y-1 pl-5 text-sm text-text-secondary"
          data-testid="connect-assistant-steps"
        >
          <li>{t(`connectAssistant.steps.${client}.open`)}</li>
          {SHARED_STEP_KEYS.map((key) => (
            <li key={key}>{t(`connectAssistant.steps.${key}`)}</li>
          ))}
        </ol>
      </div>

      <div className="mt-6 rounded-md border border-border bg-surface-hover p-3 text-sm">
        <p className="font-medium text-text-primary">{t('connectAssistant.browserGuidesTitle')}</p>
        <p className="mt-1 text-text-secondary">{t('connectAssistant.browserGuidesBody')}</p>
      </div>

      <a
        href={docUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-block text-sm text-primary hover:underline"
        data-testid="connect-assistant-doc-link"
      >
        {t('connectAssistant.fullGuide')}
      </a>

      <div className="mt-6">
        <h4 className="mb-2 text-sm font-medium text-text-primary">
          {t('connectAssistant.activeConnections')}
        </h4>
        {hasPermission('organization.manage_roles') ? (
          <ActiveConnections orgId={orgId} />
        ) : (
          <p className="text-sm text-text-placeholder" data-testid="connect-assistant-ask-admin">
            {t('connectAssistant.askAdmin')}
          </p>
        )}
      </div>
    </div>
  )
}
